import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import dateFormat from 'dateformat';
import { startOfDay, endOfDay, isToday, startOfMinute, endOfMinute } from 'date-fns';
import { useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import DefaultPreloader from '#component/preloader';
import { MapWithPreloader as Map } from '#component/map';
import { locationsSelector } from '#selector/shipment/overview';
import { fetchAppliance, fetchEventsAndLogs, clear as clearAppliance } from '#action/warehouse/appliance/appliance';
import { fetchWarehouse, clear } from '#action/warehouse/warehouse';
import DesktopView from '#component/shipment/overview/components/desktop-view';
import MobileView from '#component/shipment/overview/components/mobile-view';
import {
  fetchApplianceDevices,
  fetchApplianceDevicesDetailed,
  fetchMetricsByIdentifier,
  fetchLocations,
  applianceDevicesClear
} from '#action/warehouse/appliance/appliance-devices';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchMeta } from '#action/meta/device';
import { mapToSelect, mapSelected } from '#component/shipment/helper';
import { getChartRange, getEventsWithAcceptance, shouldShowAxisInfo } from '#component/shipment/overview/helper';
import { WARNING } from '#action/types';
import { getPeriod } from '#component/device/overview/helper';
import { mergeEvents, getAvailableDateRange } from './helper';
import Header from './header';
import { OverviewContainer } from './style';
import { setDocumentTitle, getMetricsIdentifiers, getOverviewHeaderHeight, detectExcursion, isInMonthRange } from '#helper';
import { warehouseEventType } from '#type';

const DeviceMeasurements = React.lazy(() => import('#component/shipment/overview/measurements'));

const FOUR_HOURS = 1000 * 60 * 60 * 4;
const MAP_TAB = 90;

const filterDevices = (allDevices, selectedDevices) => {
  if (selectedDevices) {
    return allDevices.filter(dd => selectedDevices.find(sd => sd.id === dd.id));
  }
  return allDevices;
};

const getPeriodFromLastMetrics = (allDevices, creationDate, startDate, endDate) =>
  allDevices.map(device => {
    const { lastMetric, lastEvent } = device;

    return getPeriod({
      lastMetric: {
        ...lastMetric?.youngest,
        timestamp: lastMetric?.timestamp
      },
      lastEvent: lastEvent ?? { timestamp: 0 },
      lastDeviceLog: { timestamp: 0 },
      startDate,
      endDate,
      beginDate: creationDate,
      expirationDate: null,
      // isDateSetFromUrl: true,
      dateRange: 0 * 24 * 60 * 60 * 1000,
      withCurrentDay: true
    });
  })
    .filter(d =>
      (new Date(creationDate) < new Date(d.end)))
    .reduce((a, b) => (a.end > b.end ? a : b), {});

const ApplianceOverview = props => {
  const { match: { params: { applianceId, tabNum, startDate, endDate } }, history } = props;
  const dispatch = useDispatch();
  const isWidthDownSm = useMediaQuery('(max-width:585px)');
  const isWidthDownMd = useMediaQuery('(max-width:960px)');
  const {
    status,
    eventsAndLogsStatus,
    metricsLoadingStatus,
    logs = [],
    allDevices,
    applianceDevices,
    settingsThresholds
  } = useSelector(s => s.applianceDevices, shallowEqual);
  const account = useSelector(s => s.account);
  const meta = useSelector(s => s.meta);
  const { appliance } = useSelector(s => s.appliance, shallowEqual);
  const { warehouse } = useSelector(s => s.warehouse, shallowEqual);
  const [selectedDevices, setSelectedDevices] = useState(null);
  const [identifiers, setIdentifiers] = useState([]);
  const [identifiersLoading, setIdentifiersLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [period, setPeriod] = useState(null);
  const [loadingsByIdentifier, setLoadingsByIdentifier] = useState({});
  const [applianceInit, setApplianceInit] = useState(false);
  const [clickedDevices, setClickedDevices] = useState([]);
  const [clickedEvents, setClickedEvents] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(getOverviewHeaderHeight());
  const [openEndDateDialog, setOpenEndDateDialog] = useState(false);

  const [map, setMap] = useState(null);
  const [chart, setChart] = useState(null);

  // track the period each datum was fetched for, so a date-range change refetches once
  const eventsFetchedPeriod = useRef(null);
  const locationsFetchedPeriod = useRef(null);
  const metricsFetchedPeriod = useRef({});
  const [sidebarTab, setSidebarTab] = useState(1);
  const [tintedMeasurements, setTintedMeasurements] = useState([]);
  const [clickedAxis, setClickedAxis] = useState([]);

  const { userType } = account;
  const tab = tabNum ? parseInt(tabNum, 10) : 0;
  const eventsLoadingStatus = eventsAndLogsStatus !== 'succeeded' || eventsAndLogsStatus === 'idle';
  const isEventsExist = selectedDevices?.some(sd => Boolean(sd?.events));

  const selectedMeasurementTab = identifiers[tabNum];
  const showAxisInfo = shouldShowAxisInfo(selectedMeasurementTab);

  const timeoutAlerts = useMemo(
    () =>
      (allDevices.filter(d => !d.lastMetric?.timestamp ||
      new Date().getTime() - new Date(d.lastMetric?.timestamp).getTime() > FOUR_HOURS)),
    [allDevices]
  );

  const devicesWithExcursions = useMemo(
    () =>
      allDevices.filter(d =>
      // a device group has an alert
        d.alerts?.length &&
      // an alert for a device is switched ON
      d.projectAlertId &&
      appliance?.alerts.find(a => {
        /*
        * derive the alert state from the already-loaded `lastMetric` (youngest values) instead
        * of the per-tab metric arrays, which are now lazy-loaded - otherwise the header warning
        * icon would only light up after the excursed metric's tab is visited.
        */
        d.excursions = a.id === d.projectAlertId ?
          detectExcursion(a, d.lastMetric) :
          [];
        return d.excursions.length;
      })),
    [allDevices, appliance]
  );

  const rangeStart = useMemo(() => (new Date(period?.start || 0)), [period]);
  const rangeEnd = useMemo(() => (new Date(period?.end || 0)), [period]);

  const isInRange = period && isInMonthRange(new Date(period.start), new Date(period.end), 1);
  const periodKey = period ? `${period.start}_${period.end}` : null;

  const replaceHistoryParams = useCallback(newParams => {
    const {
      match: { params: currentParams }
    } = props;
    const { newStartDate, newEndDate, tabNum: newTabNum = 0 } = { ...currentParams, ...newParams };
    history.replace(`/warehouse/appliance-overview/${applianceId}/t/${newTabNum}/dr/${newStartDate}/${newEndDate}`);
  }, [applianceId, history, props]);

  const tabProps = useCallback(value => ({
    value,
    tabIndex: 0,
    component: Link,
    to: `/warehouse/appliance-overview/${applianceId}/t/${value}/dr/${startDate}/${endDate}`,
    replace: true,
    onClick: () => {
      setClickedDevices([]);
      setClickedEvents([]);
    }
  }), [applianceId, startDate, endDate]);

  const setDateRange = useCallback((start, end) => {
    const newStartDate = dateFormat(startOfMinute(new Date(start)), 'isoUtcDateTime');
    const newEndDate = dateFormat(endOfMinute(new Date(end)), 'isoUtcDateTime');

    // new period: show per-region preloaders; the fetch effects refetch the visible data
    setLoadingsByIdentifier({});
    setLocationsLoading(true);
    replaceHistoryParams({ newStartDate, newEndDate });
    setPeriod({ start: newStartDate, end: newEndDate });
  }, [replaceHistoryParams, setPeriod]);

  useEffect(() => {
    const fetchApplianceData = async () => {
      await Promise.all([
        dispatch(fetchMeta()),
        dispatch(fetchAppliance(applianceId)),
        dispatch(fetchApplianceDevices(applianceId, startDate, endDate))
      ]);
      setApplianceInit(true);
    };

    fetchApplianceData();

    return () => {
      dispatch(clearAppliance());
      dispatch(applianceDevicesClear());
    };
  }, [dispatch, applianceId, startDate, endDate]);

  useEffect(() => {
    if (applianceInit && appliance.warehouse && applianceDevices?.length) {
      dispatch(fetchWarehouse(appliance.warehouse?.id));
      dispatch(fetchApplianceDevicesDetailed(
        applianceId,
        true,
        applianceDevices,
        appliance.alerts
      ));
    }
    return () => {
      dispatch(clear());
    };
  }, [dispatch, appliance, applianceId, applianceInit, applianceDevices]);

  useEffect(() => {
    setDocumentTitle('Application Overview', appliance.label);
    if (warehouse.label) {
      dispatch(updateBreadcrumb([
        { label: 'Sites', path: '/warehouse' },
        { label: `${appliance.label}` }
      ]));
    }
  }, [dispatch, appliance, warehouse]);

  useEffect(() => {
    if (appliance.creationDate && !period && allDevices.length) {
      if (!(startDate && endDate)) {
        let newStartDate;
        let newEndDate;

        const today = new Date();
        const { start, end } = getAvailableDateRange(
          appliance.creationDate,
          appliance.stoppedDate || today,
          0
        );

        newStartDate = start;
        newEndDate = end;

        const periodFromLastMetrics = getPeriodFromLastMetrics(
          allDevices,
          appliance.creationDate,
          startDate,
          endDate
        );

        if (Object.values(periodFromLastMetrics).length && !appliance.stoppedDate) {
          newStartDate = dateFormat(startOfDay(new Date(periodFromLastMetrics.start)), 'isoUtcDateTime');
          const metricsEnd = new Date(periodFromLastMetrics.end);
          const ofEnd = isToday(metricsEnd) ? endOfMinute : endOfDay;
          newEndDate = dateFormat(ofEnd(metricsEnd).toISOString(), 'isoUtcDateTime');
        }

        replaceHistoryParams({
          tabNum,
          newStartDate,
          newEndDate
        });
        setPeriod({ start: newStartDate, end: newEndDate });
      } else {
        setPeriod({ start: startDate, end: endDate });
      }
    }
  }, [
    tabNum,
    replaceHistoryParams,
    appliance.creationDate,
    appliance.stoppedDate,
    period,
    startDate,
    endDate,
    allDevices
  ]);

  useEffect(() => {
    const metricsIdentifiers = getMetricsIdentifiers(allDevices, meta);
    if (!identifiers.length && metricsIdentifiers.length) {
      setIdentifiers(metricsIdentifiers);
    }
    /*
     * Tab bar can render as soon as devices + meta are known;
     * each tab/region then manages its own preloader (no longer block on all metrics)
     */
    if (identifiersLoading && applianceInit && allDevices.length && meta.device) {
      setIdentifiersLoading(false);
    }
  }, [allDevices, meta, identifiers.length, identifiersLoading, applianceInit]);

  /*
   * Events/logs are required by every view-state (map markers, sidebar/mobile event list),
   * so load them up front, once per period.
   */
  useEffect(() => {
    if (period && isInRange && applianceInit && eventsFetchedPeriod.current !== periodKey) {
      eventsFetchedPeriod.current = periodKey;
      dispatch(fetchEventsAndLogs(applianceId, period));
    }
  }, [period, periodKey, isInRange, applianceInit, applianceId, dispatch]);

  /*
   * Locations are only needed when the map is visible: the Map tab on desktop, or the
   * always-present card grid on mobile. A metric-tab landing does not load them until Map
   * is visited.
   */
  useEffect(() => {
    const isMapVisible = tab === MAP_TAB || isWidthDownSm;
    if (period && isInRange && applianceInit && isMapVisible &&
      locationsFetchedPeriod.current !== periodKey) {
      locationsFetchedPeriod.current = periodKey;
      setLocationsLoading(true);
      dispatch(fetchLocations(applianceId, period)).then(() => setLocationsLoading(false));
    }
  }, [tab, isWidthDownSm, period, periodKey, isInRange, applianceInit, applianceId, dispatch]);

  /*
   * The active metric is fetched only when its tab (desktop) or card dialog (mobile) is the
   * current view-state, and only once per period.
   */
  useEffect(() => {
    const identifier = identifiers[tab];
    if (period && isInRange && applianceInit && identifier &&
      metricsFetchedPeriod.current[identifier] !== periodKey) {
      metricsFetchedPeriod.current[identifier] = periodKey;
      setLoadingsByIdentifier(prev => ({ ...prev, [`${identifier}Loading`]: true }));
      dispatch(fetchMetricsByIdentifier(applianceId, period, identifier))
        .then(() => setLoadingsByIdentifier(prev => ({ ...prev, [`${identifier}Loading`]: false })));
    }
  }, [tab, identifiers, period, periodKey, isInRange, applianceInit, applianceId, dispatch]);

  useEffect(() => {
    const updateHeader = async () => {
      if (map) map.resize();
      // if (chart) chart.updateDimensions();
      setHeaderHeight(getOverviewHeaderHeight());
    };

    updateHeader();
  }, [allDevices, selectedDevices, chart, map]);

  const handleEventHover = useCallback((event, item) => {
    if (chart) {
      chart.highlightTimestamp = event.type === 'mouseenter' ? new Date(item.timestamp) : null;
      chart.highlightEvent(chart.highlightTimestamp);
    }
  }, [chart]);

  const handleEventGroupHover = useCallback((event, item) => {
    if (chart) {
      chart.highlightTimestamp = event.type === 'mouseenter' ? getChartRange(item) : null;
      chart.highlightEventGroup(chart.highlightTimestamp);
    }
  }, [chart]);

  const handleEventClick = useCallback((event, item) => {
    if (tab !== MAP_TAB) {
      if (chart) {
        if (Array.isArray(item)) {
          chart.highlightTimestamp = getChartRange(item);
          chart.highlightEventGroup(chart.highlightTimestamp);
        }
        chart.moveChart(getChartRange(item));
      }
    } else if (!item.latitude || !item.longitude) {
      dispatch({
        type: WARNING,
        payload: {
          message: "Event doesn't have coordinates"
        }
      });
    } else if (map) {
      map.centerMap(item.latitude, item.longitude);
    }
  }, [tab, chart, map, dispatch]);

  const handleEventGroupClosing = useCallback((event, item) => {
    if (tab !== MAP_TAB && chart) {
      chart.resetScale();
      chart.highlightTimestamp = getChartRange(item);
      chart.highlightEventGroup(chart.highlightTimestamp);
    }
  }, [tab, chart]);

  // keeps selected devices during loading
  // reduced device duplicates
  const filteredDevices = useMemo(() => {
    const reducedDevices = {};
    allDevices.forEach(d => {
      if (!reducedDevices[d.id]) {
        reducedDevices[d.id] = { ...d };
        if (d.alerts) d.alerts = [...d.alerts];
      } else {
        const md = reducedDevices[d.id];
        if (!md.alerts) md.alerts = [];
        if (d.alerts) md.alerts = [...md.alerts, ...d.alerts];
        if (!md.alertOn && d.alertOn) md.alertOn = d.alertOn;
        if (!md.showAlertIcon && d.showAlertIcon) md.showAlertIcon = d.showAlertIcon;
      }
    });

    const devices = filterDevices(Object.values(reducedDevices), selectedDevices);
    return devices.map(m => ({ ...m, tinted: tintedMeasurements.includes(m.uid) }));
  }, [allDevices, selectedDevices, tintedMeasurements]);

  const locations = useMemo(() => (locationsSelector(allDevices)), [allDevices]);
  const events = useMemo(
    () => {
      const selectedLog = logs.filter(e =>
        e.event.id !== warehouseEventType.TRIGGER_APPLIANCE_GROUP_ALERT ||
        (e.event.id === warehouseEventType.TRIGGER_APPLIANCE_GROUP_ALERT &&
          filteredDevices.find(d => d.id === e.device.id)));
      return mergeEvents([...filteredDevices, ...selectedLog]);
    },
    [filteredDevices, logs]
  );

  const syncSelectedDevicesWithStore = useCallback((currentEvent, acceptance) => {
    // set clicked events
    if (clickedEvents.length) {
      const newClickedEvents = getEventsWithAcceptance(clickedEvents, currentEvent, acceptance);
      setClickedEvents(newClickedEvents);
    }
  }, [clickedEvents]);

  const MapComponent = <Map
    devices={filteredDevices}
    locations={locations}
    setMap={setMap}
    events={events}
    loading={locationsLoading}
    isMobile={isWidthDownSm}
    clickDevices={setClickedDevices}
    isMetricsLoaded={!identifiersLoading}
  />;

  const sidebarComponentsProps = {
    events,
    account,
    eventsLoading:
      eventsLoadingStatus || (selectedDevices?.length > 0 && !isEventsExist && !events.length),
    identifiersMap: {
      1: 'devices',
      2: 'events'
    },
    tabSelected: sidebarTab,
    setTabSelected: setSidebarTab,
    handleEventGroupHover,
    handleEventGroupClosing
  };

  const sidebarProps = {
    handleEventClick,
    handleEventHover,
    syncSelectedDevicesWithStore
  };

  const sidebarSharedProps = {
    clickedDevices,
    clickedEvents,
    setClickedDevices,
    setClickedEvents
  };

  const deviceOverviewProps = {
    parentState: loadingsByIdentifier,
    isInRange,
    history,
    isAppliance: true,
    DeviceMeasurements,
    identifiers,
    events,
    tab,
    MapComponent,
    selectedDevices: filteredDevices,
    setChart,
    tabProps,
    meta,
    rangeStart: rangeStart.toISOString(),
    rangeEnd: rangeEnd.toISOString(),
    settingsThresholds,
    account
  };

  const filterData = useMemo(
    () => {
      const devices = mapToSelect(allDevices, account.measurementSystem);
      return devices.map(m => {
        const res = ({
          ...m,
          tinted: tintedMeasurements.includes(m.uid)
        });

        if (showAxisInfo) {
          res.selectedAxis = clickedAxis?.find(ca => ca.uid === m.uid)?.axis;
        }

        return res;
      });
    },
    [allDevices, account.measurementSystem, tintedMeasurements, clickedAxis, showAxisInfo]
  );

  const filterSetData = useCallback(
    async (devices, restoreTinted) => {
      if (restoreTinted) {
        setTintedMeasurements([]);
        setClickedAxis([]);
      } else {
        // filter out the highlighted device
        const tinted = devices.filter(d => tintedMeasurements.includes(d.uid)).map(d => d.uid);
        const selectedAxises = devices.filter(d => clickedAxis.includes(d.uid)).map(d => d.uid);
        setTintedMeasurements(tinted);
        setClickedAxis(selectedAxises);
      }
      const sd = mapSelected(allDevices, devices);
      await setSelectedDevices(
        sd.map(m => ({
          ...m,
          tinted: tintedMeasurements.includes(m.uid),
          selectedAxis: clickedAxis?.find(ca => ca?.uid === m.uid)?.axis
        }))
      );
      if (map) {
        map.app.updateSelectedDevices(sd);
      }
    },
    [allDevices, map, tintedMeasurements, clickedAxis]
  );

  const sidebarDevicesProps = {
    devices: filterData,
    selectedDevices: filteredDevices,
    setDevices: filterSetData,
    rangeStart,
    rangeEnd,
    timeoutAlerts,
    devicesWithExcursions,
    account
  };

  const handleMeasurementClick = useCallback(clickedMeasurementDevices => {
    // keep clicked while loading
    const tinted = [];
    const selectedAxis = [];
    const selected = filteredDevices.map(d => d.uid);
    const deviceUids = clickedMeasurementDevices?.map(cd => cd.uid);

    if (!deviceUids) {
      setTintedMeasurements([]);
      setClickedAxis(null);
      setSidebarTab(1);

      return;
    }

    filterData.forEach(d => {
      if (selected.includes(d.uid) && !deviceUids?.includes(d.uid)) {
        tinted.push(d.uid);
      } else {
        const clickedDevice = clickedMeasurementDevices?.find(cd => cd.uid === d.uid);
        selectedAxis.push(clickedDevice);
      }
    });
    setTintedMeasurements(tinted);
    setClickedAxis(selectedAxis);

    setSidebarTab(1);
  }, [filterData, filteredDevices]);

  if (status === 'loading' || !period || !applianceInit) {
    return <DefaultPreloader />;
  }

  return (
    <OverviewContainer className="cluster overview">
      <Header
        appliance={appliance}
        userType={userType}
        account={account}
        dispatch={dispatch}
        history={history}
        timeoutAlerts={timeoutAlerts}
        isWidthDownMd={isWidthDownMd}
        isInRange={isInRange}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        setDateRange={setDateRange}
        devicesWithExcursions={devicesWithExcursions}
        selectedDevices={filteredDevices}
        openEndDateDialog={openEndDateDialog}
        setOpenEndDateDialog={setOpenEndDateDialog}
      />
      {!isWidthDownSm ?
        <DesktopView
          identifiersLoading={identifiersLoading}
          isWidthDownMd={isWidthDownMd}
          headerHeight={headerHeight}
          sidebarProps={sidebarProps}
          sidebarSharedProps={sidebarSharedProps}
          sidebarComponentsProps={sidebarComponentsProps}
          sidebarDevicesProps={sidebarDevicesProps}
          handleMeasurementClick={handleMeasurementClick}
          {...deviceOverviewProps}
        /> :
        <MobileView
          locationsLoading={locationsLoading}
          selectedDevices={filteredDevices}
          metricsLoadingStatus={metricsLoadingStatus}
          eventsLoadingStatus={eventsLoadingStatus}
          {...deviceOverviewProps}
          {...sidebarSharedProps}
          setClickedDevices={devices => {
            // "Escape"
            const d = Array.isArray(devices) ? devices : [];
            setClickedDevices(d);
          }}
        />}
    </OverviewContainer>
  );
};

export default ApplianceOverview;
